import React from 'react';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
    SidebarSeparator,
    SidebarTrigger,
    useSidebar,
} from 'src/shared/components/ui/sidebar';
import { useLocation } from 'react-router-dom';
import useAuthService from 'src/shared/hooks/useAuthService';
import { cn } from 'src/shared/helpers/utils';

// Import individual menu components
import NMlogo from 'src/assets/images/naturemetrics_logo_icon.png';
import NMlogoFull from 'src/assets/images/naturemetrics_logo.png';
import UpgradeModal from 'src/global/components/upgrade-modal/UpgradeModal';
import { useAppDispatch, useAppSelector } from 'src/store';
import { getIsUpgradeModalOpen } from 'src/global/state/site-menu/siteMenuSelector';
import { hideUpgradeModal } from 'src/global/state/site-menu/siteMenuSlice';
import { getAppRoutesAvailability, getPortfolioRoutesAvailability } from 'src/routes/state/routesSelector';
import { shouldShowGlobalNatureRiskGroup, shouldShowGlobalNaturePerformanceGroup } from 'src/routes/appRoutesHelper';
import useFeatureFlag from 'src/shared/hooks/useFeatureFlag';
import AdminMenu from './components/AdminMenu';
import SampleReceptionMenu from './components/SampleReceptionMenu';
import SupportMenu from './components/SupportMenu';
import ShopifyMenu from './components/ShopifyMenu';
import AnnouncementsMenu from './components/AnnouncementsMenu';
import UserMenu from './components/UserMenu';
import OrganisationMenu from './components/OrganisationMenu';
import GlobalNatureRiskMenu from './components/GlobalNatureRiskMenu';
import GlobalNaturePerformanceMenu from './components/GlobalNaturePerformanceMenu';
import SiteNatureRiskMenu from './components/SiteNatureRiskMenu';
import NaturePerformanceMenu from './components/NaturePerformanceMenu';
import MySitesMenuItem from './components/MySitesMenuItem';
import { getSiteNatureRiskSiteId } from './components/siteNatureRiskHelper';
import styles from './AppSidebar.module.scss';

const GlobalNatureSidebarGroup = () => {
    const appRoutesAvailability = useAppSelector(getAppRoutesAvailability);
    const portfolioRoutesAvailability = useAppSelector(getPortfolioRoutesAvailability);
    const { enabled: riskInsightsEnabled } = useFeatureFlag({ featureId: 'risk-insights' });

    const showGlobalNatureRisk = shouldShowGlobalNatureRiskGroup({ portfolioRoutesAvailability });
    const showGlobalNaturePerformance = shouldShowGlobalNaturePerformanceGroup({ portfolioRoutesAvailability });
    const showMySites = appRoutesAvailability.projects;

    if (!showGlobalNatureRisk && !showGlobalNaturePerformance && !showMySites) {
        return null;
    }

    return (
        <>
            <SidebarGroup>
                <SidebarGroupContent>
                    <SidebarMenu>
                        {showGlobalNatureRisk && <GlobalNatureRiskMenu riskInsightsEnabled={riskInsightsEnabled} />}
                        {showGlobalNaturePerformance && <GlobalNaturePerformanceMenu />}
                        {showMySites && <MySitesMenuItem />}
                    </SidebarMenu>
                </SidebarGroupContent>
            </SidebarGroup>
            <SidebarSeparator />
        </>
    );
};

const NatureSidebarGroup = () => {
    const { pathname } = useLocation();
    const showNatureRisk = Boolean(getSiteNatureRiskSiteId(pathname));

    return (
        <SidebarGroup>
            <SidebarGroupContent>
                <SidebarMenu>
                    {showNatureRisk && <SiteNatureRiskMenu />}
                    <NaturePerformanceMenu />
                </SidebarMenu>
            </SidebarGroupContent>
        </SidebarGroup>
    );
};

export function AppSidebar() {
    const { state } = useSidebar();
    const { isAuthenticated } = useAuthService();
    const { pathname } = useLocation();
    const isLoginPages = pathname.includes('login') || pathname === '/';
    const shouldShowUpgradeModal = useAppSelector(getIsUpgradeModalOpen);
    const dispatch = useAppDispatch();

    if (isLoginPages || !isAuthenticated) {
        return null;
    }

    return (
        <Sidebar collapsible='icon' className={cn('border-r', styles.sidebarGradient)}>
            <SidebarHeader className='mb-4'>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <div className='group/logo flex items-center justify-start w-full h-12 pl-2'>
                            {state === 'expanded' ? (
                                <div className='flex items-center justify-between w-full'>
                                    <img src={NMlogoFull} alt='NatureMetrics Logo' className='h-[44px]' />
                                    <SidebarTrigger className='w-[39px] h-[44px] hover:bg-grey-20' tooltip='Collapse' />
                                </div>
                            ) : (
                                <div className='relative w-[39px] h-[44px]'>
                                    <img
                                        src={NMlogo}
                                        alt='NatureMetrics Logo'
                                        className='absolute inset-0 h-full w-full transition-opacity group-hover/logo:opacity-0'
                                    />
                                    <SidebarTrigger
                                        className='-z-10  w-0 absolute inset-0 h-full opacity-0 transition-opacity group-hover/logo:opacity-100 group-hover/logo:z-10 group-hover/logo:w-full hover:bg-grey-20'
                                        tooltip='Expand'
                                    />
                                </div>
                            )}
                        </div>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>

            <SidebarContent>
                <GlobalNatureSidebarGroup />
                <NatureSidebarGroup />
            </SidebarContent>

            <SidebarFooter className='px-2'>
                <OrganisationMenu />
                <SidebarMenu>
                    <AdminMenu />
                    <SampleReceptionMenu />
                    <ShopifyMenu />
                    <AnnouncementsMenu />
                    <SupportMenu />
                    <UserMenu />
                </SidebarMenu>
            </SidebarFooter>

            {shouldShowUpgradeModal && <UpgradeModal onClose={() => dispatch(hideUpgradeModal())} />}
        </Sidebar>
    );
}

export default AppSidebar;
